import type { Passage } from "./types";

export const passage: Passage = {
  slug: "psalm-91",
  reference: "Psalm 91",
  title: "Psalm 91 meaning: the psalm the devil quotes",
  h1: "Psalm 91, and the one place the New Testament quotes it",
  description:
    "Psalm 91 has climbed past John 3:16 in the most-read lists. The one New Testament quotation of it is on the lips of the tempter, which is worth knowing before you claim it.",
  targets: [
    "psalm 91 meaning",
    "psalm 91 protection explained",
    "what does psalm 91 promise",
  ],
  published: "2026-08-27",
  standfirst:
    "It is now read more than John 3:16. It is also the only psalm the New Testament shows being quoted by the devil, and both facts belong on the same page.",
  verse: {
    text: "He who dwells in the secret place of the Most High will rest in the shadow of the Almighty. I will say of Yahweh, 'He is my refuge and my fortress; my God, in whom I trust.'",
    ref: "Psalm 91:1–2",
  },
  context: [
    {
      text: "For he will put his angels in charge of you, to guard you in all your ways. They will bear you up in their hands, so that you won't dash your foot against a stone.",
      ref: "Psalm 91:11–12",
    },
    {
      text: "He will call on me, and I will answer him. I will be with him in trouble. I will deliver him and honor him.",
      ref: "Psalm 91:15",
    },
  ],
  takeaways: [
    "Psalm 91 has risen far enough in the most-read lists to push John 3:16 down to 24th on Bible Gateway; verses 16 and 11 were in the 2025 top ten.",
    "Verses 11 and 12 are quoted once in the New Testament — by the tempter, in Matthew 4:6, as an argument for jumping off the temple.",
    "Verse 15 is the psalm's own qualifier: I will be WITH him in trouble, which assumes the trouble rather than promising its absence.",
    "Read as a blanket guarantee of physical safety it becomes untrue on contact with any hospital; read as the psalm reads itself, it is a claim about refuge inside danger.",
  ],
  body: [
    {
      type: "h2",
      id: "why-it-has-risen",
      text: "Why it has risen",
    },
    {
      type: "p",
      text: "Psalm 91 is one of the clearest signals in the whole of the search data. It has climbed steadily in Bible Gateway's annual rankings, taking two places in the 2025 top ten and pushing John 3:16 — for decades the most-quoted verse in English Christianity — down to twenty-fourth. A psalm about refuge overtaking the verse that summarises the gospel is not a fact about the psalm. It is a fact about the people reading.",
    },
    {
      type: "p",
      text: "It fits everything else in the same datasets: the most-searched terms on the largest Bible app in 2025 were love, anxiety and peace. What is being looked for is shelter.",
    },
    {
      type: "h2",
      id: "what-it-promises",
      text: "What the psalm actually promises",
    },
    {
      type: "p",
      text: "The images are military and domestic at once — a refuge, a fortress, a shield, wings to hide under, a shadow to rest in. Every one of them describes a place to be during an attack. None of them describes the attack not happening; a fortress is only useful because there is something outside it.",
    },
    {
      type: "p",
      text: "The psalm says this itself, in verse 15, in a sentence that is rarely on the poster: I will be with him in trouble. Not instead of trouble, and not before it. The structure of the whole psalm is refuge-inside-danger, and reading it that way is not a downgrade — it is what the text has been saying for three thousand years.",
    },
    {
      type: "h2",
      id: "the-quotation",
      text: "The one New Testament quotation",
    },
    {
      type: "p",
      text: "Verses 11 and 12 — the angels, the hands, the foot and the stone — appear once in the gospels. They are quoted in Matthew 4:6 by the tempter, on the pinnacle of the temple, as the reason Jesus should throw himself down: he will command his angels concerning you. The scripture is quoted accurately. That is what makes the episode worth knowing.",
    },
    {
      type: "p",
      text: "The reply is a different verse — you shall not test the Lord your God — and the point being made is not that Psalm 91 is false. It is that a true promise of protection can be turned into a demand for a demonstration, and that turning it that way is a temptation rather than faith. Anybody who intends to claim this psalm should know that the New Testament's only use of it is a warning about how it gets misused.",
    },
  ],
  misreading: {
    claim:
      "That it is a guarantee of physical safety — a psalm to pray over a house, a journey or an illness with the expectation that nothing bad can now occur.",
    correction: [
      "The reading is understandable, because the imagery is extravagant: no plague near your tent, a thousand falling at your side, angels with instructions. Taken as insurance, it produces two casualties. The first is the person for whom it does not hold, who concludes their faith was defective at the worst possible moment. The second is the psalm, which quietly stops being said in the situations it was written for.",
      "The text's own guardrails are both inside it. Verse 15 says with him in trouble, conceding the trouble in the psalm's own voice. And the New Testament's single quotation of it, in Matthew 4, is the tempter using verses 11 and 12 to argue for a jump — with the reply being that testing God is exactly what claiming a protection promise as a demonstration amounts to.",
      "What is left when the insurance reading goes is not less. It is a psalm about where a person lives — the secret place, the shadow, the refuge — while things happen. That is the difference between a promise that has to be defended after every funeral and one that can be said at the funeral.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read it whole rather than claiming a line. It is sixteen verses and takes about ninety seconds; the reason to read it whole is that verse 15 keeps the rest honest, and verse 15 is never the one on the graphic.",
    },
    {
      type: "p",
      text: "Pair it with Psalm 23 rather than with a promise list. The two are doing the same work — accompaniment inside danger rather than a route around it — and {Psalm-23-verse-by-verse} makes the same move in six verses with a pronoun shift in the middle.",
      links: { "Psalm-23-verse-by-verse": "/verse/psalm-23" },
    },
    {
      type: "note",
      text: "A psalm you can only pray when things go well is a psalm you will stop praying. This one was built to survive the other case.",
    },
  ],
  faq: [
    {
      q: "Does Psalm 91 promise physical protection?",
      a: "It promises refuge, and the psalm's own verse 15 says 'I will be with him in trouble' — which assumes the trouble. Read as a guarantee that nothing harmful can happen, it fails on contact with any hospital ward, and the New Testament's only quotation of it is the tempter using it to argue for a jump.",
    },
    {
      q: "Who quotes Psalm 91 in the New Testament?",
      a: "The tempter, in Matthew 4:6, quoting verses 11 and 12 accurately on the pinnacle of the temple. The reply is Deuteronomy — you shall not test the Lord your God. The episode is not about the psalm being false but about how a protection promise gets turned into a demand for proof.",
    },
    {
      q: "Why has Psalm 91 become so popular?",
      a: "It has climbed the most-read lists far enough to push John 3:16 to 24th on Bible Gateway. The likeliest explanation is the one visible across every dataset in the same period: the demand is for shelter and reassurance rather than for doctrine.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the psalm's opening is printed whole rather than referred to. It renders the divine name as Yahweh in verse 2, where most translations have 'the LORD'; verse 1 keeps 'the Most High' and 'the Almighty'.",
    },
  ],
  related: [
    "how-to-memorize-bible-verses",
    "how-to-stop-scrolling-at-night",
    "bible-in-a-year-on-your-phone",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-temptation",
  ],
  relatedPassages: ["psalm-23", "isaiah-41-10"],
};
